/**
 * Timed training's queries (systems/training.ts runs the queues): where a
 * unit with a `trainTime` trains, how many train there at once and how long
 * each takes. A unit trains at its city's store that would hold it (soldiers
 * and healers at the barracks, vehicles and engineers at the factory), so a
 * city's slots for a type are that building's `trainSlots`.
 */

import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { toMilli } from './fixed';
import { buildingLevel, levelStat } from './levels';
import { cityStores, storesType } from './occupancy';
import { territoryOwners } from './territory';
import type { Building, EntityId, SimState, Training, UnitType } from './types';

/**
 * Most units one city may have queued (training and waiting): caps keep a
 * real queue far shorter; this bounds what a snapshot may carry.
 */
const MAX_TRAINING_QUEUE = 32;

/** Thousandths of a second a unit of `type` takes to train; 0 = it appears at once. */
function trainTime(rules: RuleSet, type: UnitType): number {
  return toMilli(rules.units[type]?.trainTime ?? 0);
}

/** How many units train at once at building `b` (completed; 0 if it trains none). */
function trainSlots(rules: RuleSet, b: Building): number {
  const def = rules.buildings[b.type];
  if (!def || b.buildProgress < 1) return 0;
  return levelStat(def, buildingLevel(b), 'trainSlots') ?? 0;
}

/**
 * Where a `unitType` ordered at city `cityId` trains: the first of the
 * city's stores (mint order) that holds that type and has training slots.
 * Undefined for a type with no `trainTime`, or a city with no such store
 * (the unit then appears at once).
 */
function trainingBuilding(
  state: SimState,
  rules: RuleSet,
  cityId: EntityId,
  unitType: UnitType,
  owners: ReadonlyMap<HexTileMapKey, EntityId> = territoryOwners(state, rules)
): Building | undefined {
  if (trainTime(rules, unitType) === 0) return undefined;
  return cityStores(state, rules, cityId, owners).find(
    (b) => storesType(rules, b, unitType) && trainSlots(rules, b) > 0
  );
}

/**
 * The entries of `city`'s queue in a slot now (training), by id: per
 * training building, the first `trainSlots` of its entries in queue order.
 * The rest wait, as do entries whose building no longer stands in the city's
 * territory (the training system refunds those).
 */
function trainingNow(
  state: SimState,
  rules: RuleSet,
  city: Building,
  owners: ReadonlyMap<HexTileMapKey, EntityId> = territoryOwners(state, rules)
): Set<EntityId> {
  const active = new Set<EntityId>();
  const used = new Map<EntityId, number>();
  for (const t of city.training ?? []) {
    const at = state.buildings[t.at];
    const n = used.get(t.at) ?? 0;
    if (!at || owners.get(getHexTileMapKey(at.pos)) !== city.id) continue;
    if (n >= trainSlots(rules, at)) continue;
    used.set(t.at, n + 1);
    active.add(t.id);
  }
  return active;
}

/** `playerId`'s queued units (training or waiting) in all their cities, oldest city first. */
function queuedUnits(state: SimState, playerId: string): Training[] {
  const out: Training[] = [];
  for (const b of Object.values(state.buildings)) {
    if (b.owner === playerId && b.training) out.push(...b.training);
  }
  return out;
}

export { MAX_TRAINING_QUEUE, queuedUnits, trainingBuilding, trainingNow, trainSlots, trainTime };
